/**
 * INKO landing top-3 widget — shows the leaderboard's top-3 for the
 * currently-picked tier.
 *
 * Used on the /inko start screen to create a competitive pull *before* the
 * player commits to a game. Selector is a tier-tab strip (6 tiers).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, CaretRight } from "@phosphor-icons/react";
import { INKO_TIERS, getTierById } from "../../lib/inko";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function InkoLandingTop3({ tier, onChangeTier }) {
    const activeTier = tier || getTierById("padawan") || INKO_TIERS[1];
    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        setLoading(true);
        (async () => {
            try {
                const params = new URLSearchParams({
                    mode: activeTier.mode,
                    reading_mode: activeTier.reading_mode,
                    limit: "3",
                });
                const r = await axios.get(`${API}/inko/classic/leaderboard?${params}`);
                setRows(r.data?.rows || []);
                setTotal(r.data?.total_players || 0);
            } catch {
                setRows([]);
                setTotal(0);
            } finally {
                setLoading(false);
            }
        })();
    }, [activeTier.mode, activeTier.reading_mode]);

    return (
        <div
            className="mb-6 border border-white/10 bg-black/40 p-3 sm:p-4"
            data-testid="inko-landing-top3"
        >
            <div className="mb-3 flex items-center gap-2">
                <Trophy size={12} weight="bold" className="text-fa-yellow" />
                <span
                    className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow"
                    data-testid="inko-landing-top3-title"
                >
                    Top 3 · {activeTier.label}
                </span>
            </div>

            {/* Tier tab strip — horizontally scrollable on narrow screens */}
            <div className="mb-3 -mx-1 flex items-center gap-1 overflow-x-auto no-scrollbar">
                {INKO_TIERS.map((t) => {
                    const active = t.id === activeTier.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => onChangeTier && onChangeTier(t)}
                            data-testid={`inko-landing-tier-${t.id}`}
                            className={`shrink-0 font-mono text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${
                                active
                                    ? "border-fa-cream bg-fa-cream/10 text-fa-cream"
                                    : "border-white/15 text-white/40 hover:border-white/40 hover:text-fa-cream"
                            }`}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {loading ? (
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    Načítám…
                </div>
            ) : (rows?.length ?? 0) === 0 ? (
                <div
                    className="font-mono text-[10px] uppercase tracking-widest text-white/40"
                    data-testid="inko-landing-top3-empty"
                >
                    Zatím žádný záznam v této úrovni — hraj a buď první.
                </div>
            ) : (
                <div className="space-y-1" data-testid="inko-landing-top3-rows">
                    {rows.map((r, i) => (
                        <div
                            key={i}
                            className="flex items-center justify-between border border-white/5 bg-black/30 px-3 py-1.5"
                            data-testid={`inko-landing-top3-row-${i}`}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="font-mono text-[10px] text-fa-yellow/70 w-4 tabular-nums">
                                    {i + 1}
                                </div>
                                <div className="font-mono text-sm text-fa-cream truncate">
                                    {r.display_name}
                                </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                    {r.correct_count}/{r.rounds_played}
                                </div>
                                <div className="font-mono text-sm text-fa-mint tabular-nums">
                                    {r.total_score}
                                </div>
                            </div>
                        </div>
                    ))}
                    {total > 3 && (
                        <div className="pt-1 font-mono text-[9px] uppercase tracking-[0.3em] text-white/30 flex items-center gap-1">
                            <CaretRight size={9} weight="bold" />
                            {total - 3} dalších hráčů v této úrovni
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default InkoLandingTop3;
